package app.footyguide.web.page

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import app.footyguide.core.locale.LocalStrings
import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.GameRepository
import app.footyguide.domain.model.MatchStatus
import app.footyguide.web.components.AppToolbar
import app.footyguide.web.components.BottomNavBar
import app.footyguide.web.components.CrestSizes
import app.footyguide.web.components.Icons
import app.footyguide.web.components.SafeImg
import app.footyguide.web.components.club.*
import app.footyguide.web.data.StandingRow
import app.footyguide.web.data.WebGameRepository
import app.footyguide.web.route.WebRoutes
import app.footyguide.web.todayFormatted
import kotlinx.browser.window

/**
 * Stitch Design System (Screen 5: Meu Time - Hub do Torcedor e Tabela).
 * - Full-width AppToolbar (100% viewport) with tenant coloring
 * - Modular club components with zero duplication
 * - Active BottomNavBar for "Meu Time" tab
 */
@Composable
fun MyTeamPage(
    initialTeamId: String? = null,
    gameRepository: GameRepository = remember { WebGameRepository() },
    onNavigate: (String) -> Unit = {},
    onChangeTeam: () -> Unit = {}
) {
    val strings = LocalStrings.current
    val activeTeamId = initialTeamId ?: window.localStorage.getItem("fg-my-team") ?: "flamengo"
    var activeSubTab by remember { mutableStateOf(ClubSubTab.OVERVIEW) }

    val club = ClubCatalog.bySlug(activeTeamId) ?: ClubCatalog.CLUBS.first { it.slug == "flamengo" }
    var teamGames by remember(club.slug) { mutableStateOf<List<Game>>(emptyList()) }
    var standings by remember(club.slug) { mutableStateOf<List<StandingRow>>(emptyList()) }
    var isLoading by remember(club.slug) { mutableStateOf(true) }

    LaunchedEffect(club.slug) {
        isLoading = true
        try {
            val rawGames = gameRepository.getGamesByTeam(club.slug, "2026-01-01", "2026-12-31")
            teamGames = deduplicateTeamSeasonGames(rawGames)
        } catch (_: Exception) {
            teamGames = emptyList()
        }
        try {
            standings = app.footyguide.web.data.WebStandingsRepository.getStandings("brasileirao", gameRepository)
        } catch (_: Exception) {
            standings = emptyList()
        }
        isLoading = false
    }

    val today = remember { todayFormatted() }
    val recentMatches = remember(teamGames, today) {
        teamGames.filter { it.date < today || it.matchStatus == MatchStatus.FINISHED }
            .sortedBy { it.kickoff }
            .takeLast(5)
            .reversed()
    }
    val upcomingMatches = remember(teamGames, today) {
        teamGames.filter { it.date >= today && it.matchStatus != MatchStatus.FINISHED }
            .sortedBy { it.kickoff }
    }
    val nextGame = upcomingMatches.firstOrNull()

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            minHeight(100.vh)
            backgroundColor(Color("#FAFAF7"))
            paddingBottom(80.px)
        }
    }) {
        // FULL-WIDTH TOOLBAR WITH CHANGE CLUB ACTION
        AppToolbar(
            icon = {
                SafeImg(
                    src = club.logoUrl,
                    fallbackIcon = Icons.SHIELD,
                    fallbackSize = CrestSizes.TOOLBAR_FALLBACK,
                    modifier = {
                        width(CrestSizes.TOOLBAR_PX.px)
                        height(CrestSizes.TOOLBAR_PX.px)
                        property("width", CrestSizes.TOOLBAR_CSS)
                        property("height", CrestSizes.TOOLBAR_CSS)
                        property("object-fit", "contain")
                    }
                )
            },
            title = club.name,
            actions = {
                Button({
                    onClick { onChangeTeam() }
                    style {
                        fontSize(11.px)
                        fontWeight(700)
                        fontFamily("Inter, sans-serif")
                        color(Color(club.c1))
                        backgroundColor(Color("#FFFFFF"))
                        border(1.px, LineStyle.Solid, Color("#E7E7E1"))
                        padding(5.px, 10.px)
                        borderRadius(999.px)
                        cursor("pointer")
                        property("transition", "all 0.2s ease")
                    }
                }) {
                    Text(strings.changeClub)
                }
            }
        )

        // MAIN CONTENT CONTAINER
        Div({
            style {
                width(100.percent)
                maxWidth(600.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                backgroundColor(Color("#FAFAF7"))
            }
        }) {
            ClubHeroBanner(club)

            ClubSubTabsRow(
                activeTab = activeSubTab,
                onTabSelected = { activeSubTab = it },
                strings = strings
            )

            if (isLoading) {
                ClubHubSkeleton()
            } else {
                when (activeSubTab) {
                    ClubSubTab.OVERVIEW -> {
                        ClubNextMatchCard(club = club, nextGame = nextGame, onNavigate = onNavigate, strings = strings)
                        ClubRecentResultsList(club = club, matches = recentMatches, onNavigate = onNavigate, strings = strings)
                        ClubQuizCard(club = club)
                    }
                    ClubSubTab.CALENDAR -> {
                        ClubCalendarList(club = club, matches = upcomingMatches, onNavigate = onNavigate, strings = strings)
                    }
                    ClubSubTab.STANDINGS -> {
                        ClubStandingsTable(club = club, standings = standings, onNavigate = onNavigate, strings = strings)
                    }
                }
            }
        }

        // BOTTOM NAV WITH MY TEAM ACTIVE
        BottomNavBar(
            currentRoute = WebRoutes.MY_TEAM,
            onNavigate = onNavigate
        )
    }
}

// Backward-compatibility alias
typealias MyTeamSubTab = ClubSubTab

